import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Pencil, Search, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminUsers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const canEdit = user?.role === "central_admin";

  const load = () => api.get("/admin/users", { params: q ? { q } : {} }).then(r => { setItems(r.data); setSelected(new Set()); });
  useEffect(() => { load(); api.get("/organizations").then(r => setOrgs(r.data)); }, []);

  // Rows the current user is allowed to select (everyone except themselves)
  const selectableIds = useMemo(() => items.filter(u => u.id !== user?.id).map(u => u.id), [items, user?.id]);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (id, checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleAll = (checked) => {
    setSelected(checked ? new Set(selectableIds) : new Set());
  };

  const save = async () => {
    try {
      await api.patch(`/admin/users/${editing.id}`, {
        role: editing.role, organization_id: editing.organization_id, status: editing.status,
      });
      toast.success("Sparat"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fel"); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      const { data } = await api.delete(`/admin/users/${deleting.id}`);
      const extra = data?.registrations_removed ? ` (${data.registrations_removed} registreringar borttagna)` : "";
      toast.success(`Medlem borttagen${extra}`);
      setDeleting(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte ta bort"); }
  };

  const confirmBulkDelete = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { user_ids: Array.from(selected) });
      const extras = [];
      if (data.registrations_removed) extras.push(`${data.registrations_removed} registreringar`);
      if (data.skipped_self) extras.push("ditt egna konto hoppades över");
      const suffix = extras.length ? ` (${extras.join(", ")})` : "";
      toast.success(`${data.users_removed} medlemmar borttagna${suffix}`);
      setBulkOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte massradera");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <AdminLayout title="Medlemmar" actions={
      <div className="flex gap-2 items-center">
        <div className="relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <Input data-testid="user-search" placeholder="Sök namn eller e-post" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} className="pl-8 w-64"/></div>
        <Button data-testid="user-search-go" variant="outline" onClick={load}>Sök</Button>
      </div>
    }>
      {canEdit && selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5" data-testid="bulk-bar">
          <div className="text-sm font-medium text-rose-900" data-testid="bulk-count">
            {selected.size} medlem{selected.size === 1 ? "" : "mar"} valda
          </div>
          <div className="flex gap-2">
            <Button data-testid="bulk-clear" size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Rensa</Button>
            <Button data-testid="bulk-delete-open" size="sm" onClick={() => setBulkOpen(true)} className="bg-rose-600 hover:bg-rose-700 text-white">
              <Trash2 size={14} className="mr-1"/> Ta bort valda
            </Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            {canEdit && (
              <TableHead className="w-10">
                <Checkbox
                  data-testid="bulk-select-all"
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={toggleAll}
                  disabled={selectableIds.length === 0}
                  aria-label="Välj alla"
                />
              </TableHead>
            )}
            <TableHead>Namn</TableHead><TableHead>E-post</TableHead><TableHead>Arbetarekommun</TableHead>
            <TableHead>Roll</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`} data-selected={selected.has(u.id) ? "true" : undefined}>
                {canEdit && (
                  <TableCell>
                    {u.id !== user?.id ? (
                      <Checkbox
                        data-testid={`user-select-${u.email}`}
                        checked={selected.has(u.id)}
                        onCheckedChange={(v) => toggleOne(u.id, !!v)}
                        aria-label={`Välj ${u.email}`}
                      />
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest text-slate-400">Du</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.organization_name}</TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 capitalize">{u.role.replace("_", " ")}</span></TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${u.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{u.status}</span></TableCell>
                <TableCell className="text-right">
                  {canEdit && (
                    <>
                      <Button data-testid={`user-edit-${u.email}`} size="sm" variant="ghost" onClick={() => { setEditing({ ...u }); setOpen(true); }}><Pencil size={14}/></Button>
                      {u.id !== user?.id && (
                        <Button data-testid={`user-delete-${u.email}`} size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => setDeleting(u)}><Trash2 size={14}/></Button>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Redigera medlem</DialogTitle></DialogHeader>
          {editing && (<div className="space-y-3">
            <div><Label>Roll</Label>
              <Select value={editing.role} onValueChange={v => setEditing({ ...editing, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Medlem</SelectItem>
                  <SelectItem value="local_admin">Lokal admin</SelectItem>
                  <SelectItem value="central_admin">Central admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Arbetarekommun</Label>
              <Select value={editing.organization_id || ""} onValueChange={v => setEditing({ ...editing, organization_id: v })}>
                <SelectTrigger data-testid="user-org"><SelectValue placeholder="Ingen"/></SelectTrigger>
                <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={editing.status} onValueChange={v => setEditing({ ...editing, status: v })}>
                <SelectTrigger data-testid="user-status"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="active">Aktiv</SelectItem><SelectItem value="disabled">Inaktiverad</SelectItem></SelectContent>
              </Select>
            </div>
          </div>)}
          <DialogFooter><Button data-testid="user-save" onClick={save} className="bg-[#E1251B] hover:bg-[#C81D14]">Spara</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ta bort medlem?</DialogTitle>
            <DialogDescription>
              {deleting && (
                <>
                  <strong>{deleting.first_name} {deleting.last_name}</strong> ({deleting.email}) tas bort permanent
                  tillsammans med alla sina samtalsregistreringar. Åtgärden kan inte ångras.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="user-delete-cancel" variant="outline" onClick={() => setDeleting(null)}>Avbryt</Button>
            <Button data-testid="user-delete-confirm" onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Ta bort permanent</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={(v) => !v && !bulkBusy && setBulkOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ta bort {selected.size} medlem{selected.size === 1 ? "" : "mar"}?</DialogTitle>
            <DialogDescription>
              Alla valda medlemmar och deras samtalsregistreringar tas bort permanent. Åtgärden kan inte ångras.
              Ditt eget konto påverkas inte.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="bulk-delete-cancel" variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy}>Avbryt</Button>
            <Button data-testid="bulk-delete-confirm" onClick={confirmBulkDelete} disabled={bulkBusy} className="bg-rose-600 hover:bg-rose-700 text-white">
              {bulkBusy ? "Tar bort…" : `Ta bort ${selected.size} permanent`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
